/**
 * SavedFilterViews — inline UI letting the user save the current
 * filter combination under a name and recall it later.
 *
 * Pure presentation : the persistence layer lives in the
 * `usePortfolioSavedFilterViews` hook. The component receives `views`
 * (read-only list) + 3 callbacks (`onLoad`, `onSave`, `onDelete`) and
 * a `currentFilterCount` for the badge. No `useState` for the list,
 * only for the local "saving in progress" input field.
 *
 * Created in Sprint V2.B4.1 (cf. `docs/v2/PORTFOLIO_OFFCHAIN_REFONTE.md`).
 */

import { useState, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import type { SavedFilterView } from '../../hooks/usePortfolioSavedFilterViews';

interface SavedFilterViewsProps {
  views: SavedFilterView[];
  /** Called when the user clicks a saved view chip. */
  onLoad: (viewId: string) => void;
  /** Called when the user submits a new view name. Returns the saved
   *  view (or null when the name was empty / rejected). */
  onSave: (name: string) => SavedFilterView | null;
  /** Called when the user clicks the × on a chip. */
  onDelete: (viewId: string) => void;
  /** Number of currently active filters. Drives the disabled state of
   *  "save current" — saving a view with zero active filters is
   *  pointless (it's just the default state). */
  hasActiveFilters: boolean;
}

const NAME_MAX_LENGTH = 32;

export function SavedFilterViews({
  views,
  onLoad,
  onSave,
  onDelete,
  hasActiveFilters,
}: SavedFilterViewsProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const [draftName, setDraftName] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = onSave(draftName);
    if (result) {
      setDraftName('');
      setIsFormOpen(false);
    }
  };

  return (
    <div
      role="group"
      aria-label={t('portfolioDashboard.savedViewsGroup', 'Vues de filtres sauvegardées')}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        marginBottom: '12px',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        fontSize: '12px',
      }}
    >
      <Icons.Filter size={14} style={{ color: 'var(--muted)' }} aria-hidden />
      <span style={{ color: 'var(--muted)', fontWeight: 500 }}>
        {t('portfolioDashboard.savedViewsLabel', 'Vues')}
        {views.length > 0 ? ` (${views.length})` : ''}
      </span>

      {views.length === 0 && (
        <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
          {t('portfolioDashboard.savedViewsEmpty', 'Aucune vue sauvegardée')}
        </span>
      )}

      {views.map((view) => (
        <span
          key={view.id}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            fontWeight: 500,
          }}
        >
          <button
            type="button"
            onClick={() => onLoad(view.id)}
            title={t('portfolioDashboard.savedViewLoad', 'Charger cette vue')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--text)',
              fontSize: 'inherit',
              fontWeight: 'inherit',
            }}
          >
            {view.name}
          </button>
          <button
            type="button"
            onClick={() => onDelete(view.id)}
            aria-label={t('portfolioDashboard.savedViewDelete', 'Supprimer "{{name}}"', { name: view.name })}
            style={{
              background: 'none',
              border: 'none',
              padding: '0 2px',
              cursor: 'pointer',
              color: 'var(--muted)',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <Icons.X size={11} />
          </button>
        </span>
      ))}

      {!isFormOpen && (
        <button
          type="button"
          onClick={() => setIsFormOpen(true)}
          disabled={!hasActiveFilters}
          title={
            hasActiveFilters
              ? t('portfolioDashboard.savedViewSaveCurrent', 'Sauver les filtres actuels')
              : t('portfolioDashboard.savedViewNoFilters', 'Aucun filtre actif à sauver')
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 10px',
            background: 'transparent',
            color: hasActiveFilters ? 'var(--primary)' : 'var(--muted)',
            border: `1px dashed ${hasActiveFilters ? 'var(--primary)' : 'var(--border)'}`,
            borderRadius: '12px',
            fontSize: '12px',
            cursor: hasActiveFilters ? 'pointer' : 'not-allowed',
            opacity: hasActiveFilters ? 1 : 0.6,
          }}
        >
          <Icons.Plus size={11} />
          {t('portfolioDashboard.savedViewSave', 'Sauver')}
        </button>
      )}

      {isFormOpen && (
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <label htmlFor={inputId} style={{ position: 'absolute', left: '-9999px' }}>
            {t('portfolioDashboard.savedViewName', 'Nom de la vue')}
          </label>
          <input
            id={inputId}
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            maxLength={NAME_MAX_LENGTH}
            placeholder={t('portfolioDashboard.savedViewNamePlaceholder', 'Nom de la vue…')}
            autoFocus
            style={{
              padding: '4px 8px',
              border: '1px solid var(--primary)',
              borderRadius: '6px',
              fontSize: '12px',
              background: 'var(--card)',
              color: 'var(--text)',
              outline: 'none',
              minWidth: '120px',
            }}
          />
          <button
            type="submit"
            disabled={!draftName.trim()}
            aria-label={t('portfolioDashboard.savedViewConfirm', 'Confirmer')}
            style={{
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '4px 8px',
              cursor: draftName.trim() ? 'pointer' : 'not-allowed',
              opacity: draftName.trim() ? 1 : 0.5,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <Icons.Check size={12} />
          </button>
          <button
            type="button"
            onClick={() => {
              setIsFormOpen(false);
              setDraftName('');
            }}
            aria-label={t('portfolioDashboard.savedViewCancel', 'Annuler')}
            style={{
              background: 'transparent',
              color: 'var(--muted)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              padding: '4px 8px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <Icons.X size={12} />
          </button>
        </form>
      )}
    </div>
  );
}
